import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import LeadCaptureModal from "@/components/LeadCaptureModal";
import { ShieldCheck, ArrowRight, Lock, Globe, Activity } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const { locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const [showLead, setShowLead] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = () => {
    setShowLead(true);
  };

  const proceedToGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#020617] text-slate-100">
      {/* LEFT panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r border-slate-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              'url("https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")',
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#020617]/80 to-[#1E3A8A]/40" />
        <div className="absolute inset-0 evos-grid-bg opacity-60" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1E3A8A] flex items-center justify-center rounded-sm">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-black text-2xl tracking-tight">EVOS</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">IT Management Cockpit</div>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <h1 className="font-display text-5xl xl:text-6xl font-black leading-[1.05] tracking-tighter">
            Unified Control<br/>
            for Mission-Critical<br/>
            <span className="text-[#3B82F6]">Government IT.</span>
          </h1>
          <p className="text-slate-300 text-base max-w-md leading-relaxed">
            Connectivity, security, quality and availability across distributed,
            multi-vendor environments — in a single cockpit built for IT managers,
            network engineers and security analysts.
          </p>

          <div className="grid grid-cols-3 gap-3 max-w-md">
            {[
              { i: Globe, l: "SD-WAN" },
              { i: ShieldCheck, l: "SOC" },
              { i: Activity, l: "NOC" },
            ].map(({ i: Icon, l }) => (
              <div key={l} className="border border-slate-800 bg-[#0F172A]/60 backdrop-blur p-4">
                <Icon className="w-5 h-5 text-[#3B82F6] mb-3" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[11px] uppercase tracking-[0.25em] text-slate-500 font-mono">
          v1.0 • ISO 27001 • LGPD compliant
        </div>
      </div>

      {/* RIGHT panel */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md space-y-10">
          <div className="lg:hidden flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1E3A8A] flex items-center justify-center rounded-sm">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="font-display font-black text-2xl">EVOS</div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3 font-mono">
              Secure Access · Single Sign-On
            </div>
            <h2 className="font-display text-4xl font-black tracking-tighter mb-3">Sign in to EVOS</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              Authenticate with your organization Google account to access the
              operations cockpit for <span className="text-slate-200">Secretaria de Educação do Estado de Pernambuco</span>.
            </p>
          </div>

          <button
            onClick={handleLogin}
            data-testid="login-google-btn"
            className="group w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] active:scale-[0.99] transition-all text-white font-semibold py-4 px-5 flex items-center justify-between rounded-sm border border-[#1D4ED8]/40"
          >
            <span className="flex items-center gap-3">
              <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 7.1 29.3 5 24 5 16 5 9.1 9.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.1 38.6 16 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.6 35.7 44 30.2 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
              Continue with Google
            </span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

          <div className="border border-slate-800 bg-[#0F172A] p-4 flex gap-3 items-start">
            <Lock className="w-4 h-4 text-[#3B82F6] mt-0.5 flex-shrink-0" />
            <div className="text-xs text-slate-400 leading-relaxed">
              Access is restricted to authorized personnel. All activity is
              logged for compliance with <span className="text-slate-200 font-mono">LGPD</span> and{" "}
              <span className="text-slate-200 font-mono">ISO/IEC 27001</span>.
            </div>
          </div>

          <div className="pt-8 border-t border-slate-800 grid grid-cols-3 gap-4 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
            <div>{locale === "pt" ? "Região" : "Region"}<br/><span className="text-slate-300">BR-Recife</span></div>
            <div>{locale === "pt" ? "Tenants" : "Tenants"}<br/><span className="text-slate-300">4 {locale === "pt" ? "Ativos" : "Active"}</span></div>
            <div>Status<br/><span className="text-emerald-400">● {locale === "pt" ? "Operacional" : "Operational"}</span></div>
          </div>

          {/* Mini language toggle on the login screen (bottom) */}
          <div className="flex justify-center gap-2 pt-2">
            {[["en","EN"],["pt","PT"]].map(([code, lbl]) => (
              <button
                key={code}
                data-testid={`login-lang-${code}`}
                onClick={() => setLocale(code)}
                className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${locale===code ? "border-[#3B82F6] text-white bg-[#1E3A8A]/20" : "border-slate-800 text-slate-500"}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      <LeadCaptureModal
        open={showLead}
        onClose={() => setShowLead(false)}
        onContinue={proceedToGoogle}
      />
    </div>
  );
}
